import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  addEdge,
  type Node,
  type Edge,
  type Connection,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// Радикальное решение для z-index
const flowStyles = `
  /* Переопределяем всю структуру рендера React Flow */
  .react-flow__renderer {
    position: relative !important;
  }
  
  /* Nodes рендерятся первыми (нижний слой) */
  .react-flow__nodes {
    position: relative !important;
    z-index: 1 !important;
  }
  
  /* Edges рендерятся поверх nodes */
  .react-flow__edges {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    z-index: 100 !important;
    pointer-events: none !important;
  }
  
  /* Сами edges кликабельные */
  .react-flow__edge {
    pointer-events: all !important;
    z-index: 100 !important;
  }
  
  /* Handles поверх всего */
  .react-flow__handle {
    z-index: 200 !important;
  }
  
  /* Preview линии на самом верху */
  .react-flow__connection-line {
    z-index: 300 !important;
  }
  
  /* Nodes остаются кликабельными */
  .react-flow__node {
    z-index: 1 !important;
    pointer-events: all !important;
  }
  
  /* Принудительный класс для edges */
  .force-on-top {
    z-index: 1000 !important;
  }
  
  /* Еще одна попытка - через атрибуты */
  .react-flow__edge[style*="z-index"] {
    z-index: 1000 !important;
  }
`;

// Custom Device Node
interface DeviceNodeData extends Record<string, unknown> {
  label: string;
  deviceType: string;
  description?: string;
  contacts?: Array<{ id: number; label: string; type: string; }>;
}

function DeviceNode({ data }: { data: DeviceNodeData }) {
  return (
    <div className="bg-white border-2 border-blue-500 rounded-lg p-3 min-w-[100px] shadow-lg">
      <div className="text-center">
        <div className="font-bold text-blue-700 text-sm">{data.label}</div>
        <div className="text-xs text-gray-600 mt-1">{data.deviceType}</div>
        {data.description && (
          <div className="text-xs text-gray-500 mt-1">{data.description}</div>
        )}
      </div>
      
      {/* Contacts */}
      {data.contacts && (
        <div className="flex justify-around mt-2 border-t pt-2">
          {data.contacts.map((contact) => (
            <div key={contact.id} className="text-center relative">
              {/* Handle для соединений */}
              <Handle
                type="source"
                position={Position.Bottom}
                id={`${contact.id}-source`}
                style={{
                  background: '#3b82f6',
                  border: '2px solid #1e40af',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  position: 'relative',
                  transform: 'none',
                  left: 'auto',
                  top: 'auto'
                }}
                className="!relative !transform-none !left-auto !top-auto"
              />
              <Handle
                type="target"
                position={Position.Top}
                id={`${contact.id}-target`}
                style={{
                  background: '#3b82f6',
                  border: '2px solid #1e40af',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)'
                }}
              />
              <div className="text-xs mt-1">{contact.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Custom Cable Node
function CableNode({ data }: { data: DeviceNodeData }) {
  return (
    <div className="bg-white border-2 border-green-500 rounded-lg p-3 min-w-[120px] shadow-lg">
      <div className="text-center">
        <div className="bg-green-100 px-2 py-1 rounded mb-2">
          <div className="font-bold text-green-700 text-sm">{data.label}</div>
        </div>
        <div className="text-xs text-gray-600">{data.deviceType}</div>
        {data.description && (
          <div className="text-xs text-gray-500 mt-1">{data.description}</div>
        )}
      </div>
      
      {/* Cable contacts */}
      {data.contacts && (
        <div className="flex justify-around mt-2 border-t pt-2">
          {data.contacts.map((contact) => (
            <div key={contact.id} className="text-center relative">
              {/* Handle для соединений кабеля */}
              <Handle
                type="source"
                position={Position.Bottom}
                id={`${contact.id}-source`}
                style={{
                  background: '#ffffff',
                  border: '2px solid #3b82f6',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  position: 'relative',
                  transform: 'none',
                  left: 'auto',
                  top: 'auto'
                }}
                className="!relative !transform-none !left-auto !top-auto"
              />
              <Handle
                type="target"
                position={Position.Top}
                id={`${contact.id}-target`}
                style={{
                  background: '#ffffff',
                  border: '2px solid #3b82f6',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)'
                }}
              />
              <div className="text-xs font-mono mt-1">{contact.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const nodeTypes: NodeTypes = {
  device: DeviceNode,
  cable: CableNode,
};

const initialNodes: Node<DeviceNodeData>[] = [
  {
    id: '1',
    type: 'device',
    position: { x: 100, y: 100 },
    data: {
      label: 'L1',
      deviceType: 'Светильник',
      description: 'LED 12W',
      contacts: [
        { id: 1, label: 'L', type: 'input' },
        { id: 2, label: 'N', type: 'input' },
        { id: 3, label: 'PE', type: 'ground' },
      ]
    },
  },
  {
    id: '2',
    type: 'cable',
    position: { x: 300, y: 200 },
    data: {
      label: 'K1',
      deviceType: 'ВВГнг 3×1.5',
      description: 'Cable 3×1.5mm²',
      contacts: [
        { id: 1, label: 'BN', type: 'wire' },
        { id: 2, label: 'BU', type: 'wire' },
        { id: 3, label: 'GNYE', type: 'wire' },
      ]
    },
  },
  {
    id: '3',
    type: 'device',
    position: { x: 500, y: 100 },
    data: {
      label: 'A1',
      deviceType: 'Автомат',
      description: 'C16A',
      contacts: [
        { id: 1, label: '1', type: 'input' },
        { id: 2, label: '2', type: 'output' },
      ]
    },
  },
];

const initialEdges: Edge[] = [
  {
    id: 'e1-2',
    source: '1',
    target: '2',
    sourceHandle: '1-source', // L контакт светильника
    targetHandle: '1-target', // BN контакт кабеля
    type: 'smoothstep',
    style: { stroke: '#e74c3c', strokeWidth: 3 },
    label: 'L → BN',
  },
];

export default function FlowDemo() {
  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const onConnect = useCallback(
    (params: Connection) => {
      const newEdge = {
        ...params,
        type: 'smoothstep',
        style: { stroke: '#3b82f6', strokeWidth: 2 },
        label: `${params.sourceHandle} → ${params.targetHandle}`,
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges],
  );

  return (
    <div className="h-screen w-full">
      {/* CSS для правильного z-index линий */}
      <style dangerouslySetInnerHTML={{ __html: flowStyles }} />
      
      <div className="bg-gray-100 p-4 border-b">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">React Flow Demo - Electric Schema</h1>
            <p className="text-gray-600">Простая демонстрация React Flow для электрических схем</p>
            <p className="text-sm text-purple-600 mt-1">🚀 Радикальный подход: абсолютное позиционирование edges поверх nodes</p>
          </div>
          <div className="flex space-x-2">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              ← Back to Main
            </Link>
            {window.location.pathname.includes('/projects/') && (
              <Link 
                to={window.location.pathname.replace('/flow', '')}
                className="text-green-600 hover:text-green-800"
              >
                ← Back to Old System
              </Link>
            )}
          </div>
        </div>
      </div>
      
      <div className="h-[calc(100vh-80px)]">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          fitView
          className="bg-gray-50"
          elevateEdgesOnSelect={true}
          elevateNodesOnSelect={false}
          defaultEdgeOptions={{
            style: { zIndex: 1000 },
            className: 'force-on-top'
          }}
        >
          <Controls />
          <MiniMap />
          <Background variant={"dots" as any} gap={20} size={1} />
        </ReactFlow>
      </div>
    </div>
  );
} 